import classNames from 'classnames'
import React, {
  ChangeEventHandler,
  ComponentProps,
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { GradientLiveBorder } from '../GradientLiveBorder/GradientLiveBorder'
import s from './BaseInput.scss'
import { useFormFieldContext } from './FormFieldContext'

type BaseInputProps = (ComponentProps<'textarea'> | ComponentProps<'input'>) & {
  suggestion?: string
  suffix?: React.ReactNode
  bottomContent?: React.ReactNode
  minRows?: number
  maxRows?: number
  autoGrow?: boolean
  as?: 'input' | 'textarea'
  skin?: 'standard' | 'harmony'
}

export const BaseInput = forwardRef<HTMLTextAreaElement | HTMLInputElement, BaseInputProps>(
  (
    {
      suggestion,
      className,
      value,
      onChange,
      suffix,
      bottomContent,
      minRows = 1,
      maxRows = 3,
      autoGrow,
      as: Tag = 'textarea',
      skin = 'standard',
      ...restProps
    },
    ref,
  ) => {
    const suggestionText = value && typeof value === 'string' && suggestion?.startsWith(value) ? suggestion : ''
    const [paused, setPaused] = useState(false)

    const sizerRef = useRef<HTMLTextAreaElement>(null)
    const [height, setHeight] = useState<number | undefined>(undefined)
    const [isAtMax, setIsAtMax] = useState(false)

    const handleChange: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = e => {
      setPaused(true)
      onChange?.(e as React.ChangeEvent<HTMLTextAreaElement> & React.ChangeEvent<HTMLInputElement>)
    }

    const updateHeight = useCallback(() => {
      if (!autoGrow || !sizerRef.current) {
        return
      }

      const sizer = sizerRef.current
      sizer.style.height = '0'
      const scrollHeight = sizer.scrollHeight
      sizer.style.height = ''

      const style = getComputedStyle(sizer)
      const lineHeight = parseFloat(style.lineHeight) || 24
      const paddingTop = parseFloat(style.paddingTop) || 0
      const paddingBottom = parseFloat(style.paddingBottom) || 0

      const minHeight = lineHeight * minRows + paddingTop + paddingBottom
      const maxHeight = lineHeight * maxRows + paddingTop + paddingBottom

      setHeight(Math.min(Math.max(scrollHeight, minHeight), maxHeight))
      setIsAtMax(scrollHeight > maxHeight)
    }, [autoGrow, minRows, maxRows])

    useLayoutEffect(() => {
      updateHeight()
    }, [value, updateHeight])

    const { status } = useFormFieldContext()

    const singleLine = Tag === 'input'
    const isDisabled = 'disabled' in restProps && restProps.disabled

    const borderSkin = isDisabled ? 'disabled' : status === 'error' ? 'error' : skin

    return (
      <GradientLiveBorder paused={paused} skin={borderSkin}>
        <div className={s.content}>
          <div className={classNames(s.textareaWrapper, { [s.singleLine]: singleLine, [s.withSuffix]: !!suffix })}>
            <div className={s.inputSlot}>
              <span className={classNames(s.suggestion, { [s.singleLine]: singleLine })}>{suggestionText}</span>
              {autoGrow && (
                <textarea
                  ref={sizerRef}
                  className={classNames(
                    s.textarea,
                    s.sizer,
                    s.autoGrow,
                    { [s.noBottomPadding]: !!bottomContent },
                    className,
                  )}
                  style={{ boxSizing: 'border-box' }}
                  value={value}
                  readOnly
                  tabIndex={-1}
                  aria-hidden
                />
              )}

              <Tag
                {...(restProps as any)}
                value={value}
                className={classNames(
                  s.textarea,
                  {
                    [s.singleLine]: singleLine,
                    [s.autoGrow]: autoGrow,
                    [s.noBottomPadding]: !!bottomContent,
                  },
                  className,
                )}
                onChange={handleChange}
                ref={ref as any}
                style={
                  autoGrow && height
                    ? { height, boxSizing: 'border-box', overflow: isAtMax ? 'auto' : undefined }
                    : undefined
                }
              />
            </div>
            {suffix && <div className={classNames(s.suffixContainer, { [s.singleLine]: singleLine })}>{suffix}</div>}
          </div>
          {bottomContent && <div className={s.bottomContent}>{bottomContent}</div>}
        </div>
      </GradientLiveBorder>
    )
  },
)
